// Import necessary modules and types
import '../types';
import {Component, createRef} from 'react';
import Spinner from '../Spinner';
import { ExpandedProduct, Variant } from '../types';
import ProductCard from './ProductCard';
import FilterDrawer from './FilterDrawer';
import SortDropDown from './SortDropDown';

// Define the state interface
interface CollectionProductGridState {
  filterable_sortable_products: ExpandedProduct[];
  filter_drawer_open: boolean;
  product_showing: number;
  returning_from_product: boolean;
  is_filtered: boolean;
  loading_more: boolean;
  has_more_products: boolean;
}

// Define the main component
export default class CollectionProductGrid extends Component<any, CollectionProductGridState> {
  // Initialize class properties
  private currentLanguage: string = document.documentElement.lang;
  private currentPage: number = 1;
  private currentCollectionUrl: string = location.href;
  private sizeFilter: string[] = [];
  private brandFilter: string[] = [];
  private currentSort: string = 'manual';
  private products: ExpandedProduct[] = [];
  private bestSellersArray: ExpandedProduct[] = [];
  private readonly lastProduct: string = '';
  private readonly is_axial_debug: boolean = false;
  private readonly isSearch: boolean = this.props.type === 'search';

  // Initialize component state
  state: CollectionProductGridState = {
    filterable_sortable_products: [],
    filter_drawer_open: false,
    product_showing: 16,
    returning_from_product: false,
    is_filtered: false,
    loading_more: false,
    has_more_products: true,
  };

  // Constructor for the component
  constructor(props: any) {
    super(props);
    this.is_axial_debug = localStorage.getItem('axial_debug') === 'true';
    this.lastProduct = new URL(location.href).searchParams.get('last_product') ?? '';
  }

  componentDidMount() {
    // Initialize properties and fetch initial data
    this.lastProduct ? this.initializeWithLastProduct() : this.fetchPageProducts(1);
    this.setupWindowFunctions();
    this.setupInfiniteScroll();
    this.scrollToAnchor();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  // Initialize the component with the last product viewed
  initializeWithLastProduct() {
    if (this.currentCollectionUrl.includes('?')) {
      this.currentCollectionUrl = this.currentCollectionUrl.split('?')[0];
    }

    const productsJson = localStorage.getItem('products');
    if (productsJson) {
      let collectionProductsRaw = JSON.parse(productsJson);
      let collectionProducts = collectionProductsRaw[this.currentCollectionUrl];
      if (collectionProducts) {
        this.products = collectionProducts;
        this.setState({filterable_sortable_products: this.products});
      }
    } else {
      this.fetchPageProducts(1);
    }

    // Animate the product grid
    const animate = () => {
      if (this.state.product_showing > this.products.length) {
        this.handleUrlParameters();
        return;
      } else {
        this.setState((prevState) => ({
          product_showing: prevState.product_showing + 16,
        }));
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }

  // Handle URL parameters
  handleUrlParameters() {
    const url = new URL(location.href);
    if (url.searchParams.get('sort_by')) {
      this.currentSort = url.searchParams.get('sort_by')!;
      this.changeSort(this.currentSort);
    }

    if (url.searchParams.get('size')) {
      const sizes = url.searchParams.get('size')!.split(',').map((size) => size.replaceAll('"', ''));
      this.sizeFilter = sizes;
      this.filterProducts();
    }
  }

  // Scroll to anchor if grid parameter is present in URL
  scrollToAnchor = () => {
    const anchorToProductGrid = "grid";
    const params = new URL(document.location.toString()).searchParams;

    if (params.has(anchorToProductGrid)) {
      const element = document.querySelector(".CollectionMain") as HTMLElement;
      if (element) {
        const elementTop = element.offsetTop;
        const offsetPosition = elementTop - 300;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });

        // Update header to simulate scroll
        const header = document.querySelector('header.site-header') as HTMLElement;
        const shopifySectionHeader = document.getElementById('shopify-section-header');

        if (shopifySectionHeader) {
          shopifySectionHeader.dataset.scrolled = "true";
        }

        if (header) {
          header.dataset.scrolled = "true";
          header.dataset.pointZero = "true";
        }
      }
    }
  }

  // Set up window functions for filtering and sorting
  setupWindowFunctions() {
    window.addSizeFilter = this.addSizeFilter;
    window.removeSizeFilter = this.removeSizeFilter;
    window.addBrandFilter = this.addBrandFilter;
    window.removeBrandFilter = this.removeBrandFilter;
    window.filterProducts = this.filterProducts;
    window.stopFiltering = this.stopFiltering;
    window.closeFilterDrawer = this.closeFilterDrawer;
    window.changeSort = this.changeSort;
  }

  // Set up infinite scroll functionality
  setupInfiniteScroll = () => {
    window.addEventListener('scroll', this.handleScroll);
  }

  // Handle scroll events for infinite loading
  handleScroll = () => {
    if (this.state.loading_more || !this.state.has_more_products) {
      return;
    }

    const collectionGrid = document.getElementById('collection-product-grid');
    if (!collectionGrid) {
      return;
    }

    const gridRect = collectionGrid.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate distance from bottom of container to bottom of viewport
    const distanceFromBottom = gridRect.bottom - viewportHeight;

    // Load more when user reaches 300px above the bottom of container
    if (distanceFromBottom <= 300) {
      this.loadMoreProducts();
    }
  }

  // Load more products for infinite scroll
  loadMoreProducts = async () => {
    if (this.state.loading_more || !this.state.has_more_products) {
      return;
    }

    this.setState({ loading_more: true });

    const nextPage = this.currentPage + 1;
    await this.fetchPageProducts(nextPage, true);
  }

  // Add a size filter
  addSizeFilter = (size: string) => {
    const url = new URL(location.href);
    const sizeArray = url.searchParams.get('size');
    url.searchParams.set('size', sizeArray ? `${sizeArray},"${size}"` : `"${size}"`);
    history.pushState({}, '', url.toString());
    this.sizeFilter.push(size);
  }

  // Remove a size filter
  removeSizeFilter = (size: string) => {
    const url = new URL(location.href);
    let sizeArray = url.searchParams.get('size');

    if (sizeArray) {
      // Use regular expressions to remove the size from the string
      sizeArray = sizeArray.replace(new RegExp(`"${size}"(,)?`, 'g'), '');

      // Remove leading and trailing commas
      sizeArray = sizeArray.replace(/^,|,$/g, '');

      url.searchParams.set('size', sizeArray);
      history.pushState({}, '', url.toString());
    }

    this.sizeFilter = this.sizeFilter.filter((item: string) => item !== size);
  }

  // Add a brand filter
  addBrandFilter = (brand: string) => {
    this.brandFilter.push(brand);
  }

  // Remove a brand filter
  removeBrandFilter = (brand: string) => {
    this.brandFilter = this.brandFilter.filter((item: string) => item !== brand);
  }

  // Filter the products based on size and brand filters
  filterProducts = () => {
    let filteredProducts: ExpandedProduct[] = this.products;

    if (this.sizeFilter.length > 0) {
      filteredProducts = filteredProducts.filter((product: ExpandedProduct) =>
        product.filterable?.size.some((size: string) => this.sizeFilter.includes(size))
      );
    }

    if (this.brandFilter.length > 0) {
      filteredProducts = filteredProducts.filter((product: ExpandedProduct) =>
        this.brandFilter.includes(product.filterable.vendor)
      );
    }

    this.setState({ filterable_sortable_products: filteredProducts });
  }

  // Fetch best-selling products
  fetchBestSellers = async () => {
    const currentURL = location.href;
    const cleanedUrl = currentURL.split('?')[0];
    const bestSeller = cleanedUrl + '?view=react&sort_by=best-selling';
    const response = await fetch(bestSeller);
    const json = await response.json();
    this.bestSellersArray = json.products;
  }

  // Change the sorting order
  changeSort = (sort: string) => {
    if (sort !== this.currentSort) {
      this.currentSort = sort;
      let url = new URL(location.href);
      url.searchParams.set('sort_by', sort);
      history.pushState({}, '', url.toString());
      this.sortProducts();
    }
  }

  // Sort the products based on the current sort order
  sortProducts = async () => {
    const { filterable_sortable_products } = this.state;

    switch (this.currentSort) {
      case 'price-ascending':
        this.setState({
          filterable_sortable_products: filterable_sortable_products.sort((a, b) => a.prices[0]!.price - b.prices[0]!.price),
        });
        break;
      case 'price-descending':
        this.setState({
          filterable_sortable_products: filterable_sortable_products.sort((a, b) => b.prices[0]!.price - a.prices[0]!.price),
        });
        break;
      case 'best-selling':
        await this.fetchBestSellers();
        this.buildProductArray(this.bestSellersArray);
        break;
    }
  }

  // Stop filtering and clear filters
  stopFiltering = () => {
    this.sizeFilter = [];
    this.brandFilter = [];
    this.setState({ is_filtered: false });
  }

  // Close the filter drawer
  closeFilterDrawer = () => {
    this.setState({ filter_drawer_open: false });
  }

  // Fetch products for the current page
  fetchPageProducts = async (page?: number, append: boolean = false) => {
    if (this.currentCollectionUrl.includes('?')) {
      this.currentCollectionUrl = this.currentCollectionUrl.split('?')[0];
    }

    const currentPage = page || 1;
    let response = null;
    let data = null;

    if (!this.isSearch) {
      response = await fetch(`${this.currentCollectionUrl}?view=react&page=${currentPage}`);
    } else {
      // Get q parameter from URL
      const url = new URL(location.href);
      const q = url.searchParams.get('q');
      response = await fetch(`/search?type=product&q=${q}&view=react&page=${currentPage}`);
    }

    data = await response.json();
    const products = data.products;

    // Update current page
    this.currentPage = currentPage;

    // Check if there are more products to load
    const hasMoreProducts = products.length === 24; // Assuming 24 products per page
    this.setState({ has_more_products: hasMoreProducts });

    this.buildProductArray(products, currentPage, append);
  }

  // Generate a UUID (Universally Unique Identifier)
  uuidv4 = () => {
    // ID generation code, copied from StackOverflow
    return ([1e7][0] + -1e3 + -4e3 + -8e3 + -1e11).toString().replace(/[018]/g, (c) =>
      (parseInt(c, 10) ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> +c / 4).toString(16)
    );
  }

  // Build the product array based on retrieved products
  buildProductArray(products: ExpandedProduct[], page?: number, append: boolean = false) {
    if (products.length === 0) {
      this.setState({ loading_more: false });
      return;
    }

    const expandedProduct: ExpandedProduct[] = [];
    const currentPage = page ?? this.currentPage;

    for (const product of products) {
      // Process product data
      const uniqueImages = [...new Set(product.variant_images.filter((image) => !image.includes('no-image')))];
      const variants: Variant[] = [];

      for (const image of uniqueImages) {
        const variant = product.variants.find((v) => v.featured_image?.src === image && v.available);
        if (variant) {
          variants.push(variant);
        }
      }

      if (this.is_axial_debug) {
        console.log('-------------------------------');
        console.log('Product handle:', product.handle);
        console.log('Product title:', product.title);
        console.log('Product:', product);
        console.log('Unique images:', uniqueImages);
        console.log('Variants:', variants);
        console.log('-------------------------------');
      }

      const variantPrices = variants.map((variant) => ({
        compare_at_price: variant.compare_at_price || 0,
        price: variant.price,
      }));

      let productIndex = 0;
      // Increase the number of variants to display per product by increasing the number in Math.min
      for (let position = 0; position < Math.min(variants.length, window.variantPerProductCard); position++) {
        const uuid = this.uuidv4();
        expandedProduct.push({
          id: product.id,
          index: productIndex,
          uuid,
          handle: product.handle,
          title: product.title,
          vendor: product.vendor,
          soon_available: product.soon_available,
          by_vero: product.by_vero,
          position,
          variants,
          images: uniqueImages,
          fallback_image: product.images[0],
          variant_images: product.variant_images,
          prices: variantPrices as [],
          reorder: product?.reorder,
          filterable: {
            // color: product.options![0].values,
            size: product.options![0].values,
            vendor: product.vendor,
          },
          page: currentPage,
        });
        productIndex++;
      }
    }

    // Handle append mode for infinite scroll
    if (append) {
      this.products = [...this.products, ...expandedProduct];
      this.setState({
        filterable_sortable_products: [...this.state.filterable_sortable_products, ...expandedProduct],
        loading_more: false
      });
    } else {
      this.products = expandedProduct;
      this.setState({
        filterable_sortable_products: expandedProduct,
        loading_more: false
      });
    }

    if (this.is_axial_debug) {
      console.log('Expanded product:', expandedProduct);
    }

    // Save products to local storage for faster loading when navigating back to the collection
    try {
      let data = {
        [this.currentCollectionUrl]: this.products,
      }
      localStorage.setItem('products', JSON.stringify(data));
    } catch (e) {
      console.error('Error saving products to local storage:', e);
    }

    // Animate the product grid
    const animate = () => {
      if (this.state.product_showing > this.products.length) {
        this.handleUrlParameters();
        return;
      } else {
        this.setState((prevState) => ({
          product_showing: prevState.product_showing + 16,
        }));
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }

  // Render the component
  render() {
    return (
      <div className={'product-grid-wrapper'}>
        {!this.isSearch && (
          <>
            <div className={'filter-and-sort'}>
              {this.sizeFilter.length + this.brandFilter.length > 0 ? (
                  <p className={'filter-button'}
                     onClick={() => this.setState({filter_drawer_open: !this.state.filter_drawer_open})}>
                    {window.currLanguage === 'fr' ? 'FILTRER' : 'FILTER'} ({this.sizeFilter.length + this.brandFilter.length})
                  </p>
              ) : (
                  <p className={'filter-button'}
                     onClick={() => this.setState({filter_drawer_open: !this.state.filter_drawer_open})}>
                    {window.currLanguage === 'fr' ? 'FILTRER' : 'FILTER'}
                  </p>
              )}
              <SortDropDown/>
            </div>
            <FilterDrawer open={this.state.filter_drawer_open}/>
            <div className={'intersection'} hidden={this.state.filterable_sortable_products.length > 0}>
              <Spinner/>
            </div>
          </>
        )}
        <div className={'product-grid'} hidden={this.state.filterable_sortable_products.length === 0}>
          {this.state.filterable_sortable_products.map((product: ExpandedProduct, index) => {
            if (product.index < this.state.product_showing) {
              return <ProductCard checkpoint={index % 4 === 0 && index !== 0} key={product.uuid} product={product}
                                  currentCollectionUrl={this.currentCollectionUrl} lastProduct={this.lastProduct}/>;
            }
          })}
        </div>
        <div className={'returning-modal'} data-open={this.state.returning_from_product}>
          <h6>{this.currentLanguage === 'en' ? 'We return to where you left off...' : 'Nous vous retournons où vous aviez arrêté ...'}</h6>
        </div>
        {this.state.loading_more && (
          <div className={'loading-more-products tw-flex tw-justify-center tw-p-5'}>
            <Spinner />
          </div>
        )}
      </div>
    );
  }
}
